// Photoshoots — full CRUD + folded-in Featured config (Style of the Week
// + Trending shelf). Sidebar lost the "Featured" entry in PX-69; this
// page is now the single place to manage everything related to which
// photoshoots are shown on the iOS Styles tab.

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { APIError, request } from '@/lib/api'
import { MediaDropzone } from '@/components/MediaDropzone'
import { DragHandle, TabSwitcher } from '@/components/FormControls'
import { moveItem } from '@/lib/reorder'
import { PhotoshootsTable } from './photoshoots/PhotoshootsTable'
import { PlanChips } from './photoshoots/PlanChips'
import type {
  AdminAiProfileGuidelinesResponse,
  AdminAiProfileGuidelinesUpdate,
  AdminAiProfilePreviewResponse,
  AdminAiProfilePreviewUpdate,
  AiProfileGuidelineItem,
  AdminCategoryOrderResponse,
  AdminCategoryOrderUpdate,
  AdminHeroSlot,
  AdminHeroSlotsResponse,
  AdminHeroSlotsUpdate,
  AdminModelResponse,
  CategoryItem,
  AdminOnboardingFiltersResponse,
  AdminOnboardingFiltersUpdate,
  AdminFunnelV2Response,
  AdminFunnelV2Update,
  AdminPhotoshootCreate,
  AdminPhotoshootResponse,
  AdminPhotoshootUpdate,
  AdminPhotoshootVariableCreate,
  AdminPhotoshootVariableOptionCreate,
  AdminPhotoshootVariableOptionUpdate,
  AdminPhotoshootVariableResponse,
  AdminPhotoshootVariableUpdate,
  OnboardingFilterItem,
  VariableInputKind,
  AdminPhotoshootVariantCreate,
  AdminPhotoshootVariantResponse,
  AdminPhotoshootVariantUpdate,
  AdminPlanResponse,
  PhotoSlot,
  AdminTrendingShelfResponse,
  AdminTrendingShelfUpdate,
} from '@/types/api'

const SLUG_RE = /^[a-z0-9-]+$/
// Standard output formats offered in-app (PX-177). Empty policy = all of these
// are selectable; a subset limits the choice; a single value locks it. A custom
// ratio (e.g. 2:3) outside this set is allowed too.
const STANDARD_RATIOS = ['1:1', '9:16', '16:9', '3:4', '4:3']
const ASPECT_RE = /^[0-9]{1,2}:[0-9]{1,2}$/

function Toggle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  label: string
}) {
  return (
    <label className={`inline-flex cursor-pointer items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}>
      <span className="text-xs font-medium text-ink-500">{label}</span>
      <span
        role="switch"
        aria-checked={checked}
        onClick={(e) => {
          e.stopPropagation()
          if (!disabled) onChange(!checked)
        }}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${checked ? 'bg-brand-500' : 'bg-ink-200'}`}
      >
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition ${checked ? 'left-[18px]' : 'left-0.5'}`} />
      </span>
    </label>
  )
}

export function PhotoshootsPage() {
  const queryClient = useQueryClient()

  const listQuery = useQuery({
    queryKey: ['admin', 'photoshoots'],
    queryFn: () => request<AdminPhotoshootResponse[]>('/admin/photoshoots'),
  })

  // Plans for the in-row Allowed-plans quick-edit (PX-385). Same query key as
  // the editor modal → cache shared, no extra request when both are open.
  const plansQuery = useQuery({
    queryKey: ['admin', 'catalog', 'plans'],
    queryFn: () => request<AdminPlanResponse[]>('/admin/catalog/plans'),
  })

  const patchMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: AdminPhotoshootUpdate }) =>
      request<AdminPhotoshootResponse>(`/admin/photoshoots/${id}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'photoshoots'] })
    },
  })

  const [editorState, setEditorState] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; photoshoot: AdminPhotoshootResponse }
    | null
  >(null)

  // PX-435: lifecycle tabs — Pending / Approved / Archived / All. The destination
  // (kind) filter moved INTO the table's Filters panel (PX-448), so this page
  // only owns the lifecycle status tab now.
  const [statusTab, setStatusTab] = useState<'all' | 'pending' | 'approved' | 'archived'>('all')
  const allPhotoshoots = listQuery.data ?? []
  const filteredPhotoshoots = allPhotoshoots.filter(
    (p) => statusTab === 'all' || p.status === statusTab,
  )
  const statusCount = (s: string) => allPhotoshoots.filter((p) => p.status === s).length

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Photoshoots</h1>
        <p className="text-sm text-ink-400">Full editor + Featured shelves config.</p>
      </header>

      <section className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <HeroSlotsCard photoshoots={listQuery.data ?? []} />
        <TrendingCard photoshoots={listQuery.data ?? []} />
        <CategoriesCard />
        <AiProfilePreviewCard photoshoots={listQuery.data ?? []} />
      </section>

      <section className="mb-8">
        <AiProfileGuidelinesCard />
      </section>

      <section className="mb-8">
        <FunnelV2Card />
      </section>

      <section className="mb-8">
        <OnboardingFiltersCard photoshoots={listQuery.data ?? []} />
      </section>

      {patchMutation.error instanceof APIError && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {patchMutation.error.message}
        </div>
      )}

      {/* Lifecycle status filter (PX-435) + create button on the same row (PX-472)
          so "+ New photoshoot" stays in reach next to the table — no scroll up.
          Destination (kind) filtering lives in the table's Filters panel (PX-448). */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <TabSwitcher
          tabs={[
            { id: 'all', label: `All (${allPhotoshoots.length})` },
            { id: 'pending', label: `Pending (${statusCount('pending')})` },
            { id: 'approved', label: `Approved (${statusCount('approved')})` },
            { id: 'archived', label: `Archived (${statusCount('archived')})` },
          ] as const}
          current={statusTab}
          onChange={setStatusTab}
        />
        <button
          type="button"
          onClick={() => setEditorState({ mode: 'create' })}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600"
        >
          + New photoshoot
        </button>
      </div>

      <PhotoshootsTable
        photoshoots={filteredPhotoshoots}
        plans={plansQuery.data ?? []}
        isLoading={listQuery.isLoading}
        patching={patchMutation.isPending}
        onOpen={(ps) => setEditorState({ mode: 'edit', photoshoot: ps })}
        onPatch={(id, body) => patchMutation.mutate({ id, body })}
      />

      {editorState && (
        <PhotoshootEditorModal
          mode={editorState.mode}
          initial={editorState.mode === 'edit' ? editorState.photoshoot : null}
          allCategories={[
            ...new Set((listQuery.data ?? []).map((p) => p.category).filter((c): c is string => !!c)),
          ].sort()}
          onClose={() => setEditorState(null)}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Featured shelves — folded in from Featured.tsx (PX-69).
// ---------------------------------------------------------------------------

// A "Hero N" boundary marker in the catalog list (PX-467) — visualises where the
// iOS feed inserts a hero banner: Hero 1 at the very top, then one after every 3
// catalog shelves (matches the FeedSlot interleave + the floor(N/3)+1 cadence).
function HeroBoundary({ n, top }: { n: number; top?: boolean }) {
  return (
    <div className="flex items-center gap-2 border-b border-ink-50 bg-ink-50 px-3 py-1.5">
      <span className="rounded bg-brand-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
        Hero {n}{top ? ' (top)' : ''}
      </span>
      <span className="h-px flex-1 bg-ink-200" />
    </div>
  )
}

// Catalog shelves order (PX-460/463/467). The category SET is derived from distinct
// photoshoot.category values (active rows of the kind) — this card only arranges
// their ORDER (drag-sort), separately for Styles and Upscale. The order drives
// the iOS feed shelf order; the count of categories drives how many hero slots
// the operator gets (floor(N/3)+1, see HeroSlotsCard).
function CategoriesCard() {
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<'style' | 'upscale'>('style')
  const cfg = useQuery({
    queryKey: ['admin', 'featured', 'categories', kind],
    queryFn: () => request<AdminCategoryOrderResponse>(`/admin/featured/categories?kind=${kind}`),
  })
  const save = useMutation({
    mutationFn: (body: AdminCategoryOrderUpdate) =>
      request<AdminCategoryOrderResponse>(`/admin/featured/categories?kind=${kind}`, { method: 'PATCH', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'featured', 'categories', kind] }),
  })

  // Local draft so a drag doesn't fire a request per move — Save persists the
  // whole order at once. Re-synced whenever the server list changes (or kind flips).
  const [draft, setDraft] = useState<CategoryItem[]>([])
  const [mode, setMode] = useState<'auto' | 'manual'>('manual')
  const [expanded, setExpanded] = useState(false)
  useEffect(() => {
    if (cfg.data) {
      setDraft(cfg.data.items)
      setMode(cfg.data.mode)
    }
  }, [cfg.data])

  const reorder = (sourceName: string, targetName: string) => {
    if (mode !== 'manual') return
    setDraft((cur) => moveItem(cur, sourceName, targetName, (c) => c.name))
  }

  // Alphabetical preview in auto; the drag order in manual (draft is never
  // mutated in auto, so toggling back to manual restores the arrangement).
  const displayList = mode === 'auto'
    ? [...draft].sort((a, b) => a.name.localeCompare(b.name))
    : draft

  const serverOrder = (cfg.data?.items ?? []).map((c) => c.name).join(' ')
  const draftOrder = draft.map((c) => c.name).join(' ')
  const dirty = mode !== (cfg.data?.mode ?? 'manual') || draftOrder !== serverOrder
  // floor(N/3)+1 hero slots become available for this many categories (mirrors
  // the backend cadence) — shown so the operator sees the link to HeroSlotsCard.
  const heroSlots = Math.floor(draft.length / 3) + 1

  return (
    <div className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <h2 className="text-base font-semibold tracking-tight">Catalog order</h2>
          <p className="mt-1 text-xs text-ink-400">
            How the shelves on the iOS {kind === 'style' ? 'Styles' : 'Upscale'} feed are ordered.
            Auto = alphabetical; Manual = drag to arrange. The Hero markers show where a hero
            banner is inserted (one at the top, then after every 3 shelves).
          </p>
        </div>
        <span className="ml-3 shrink-0 text-ink-400">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
      <>
      <div className="mt-3 inline-flex gap-1 rounded-lg bg-ink-100 p-1">
        {(['style', 'upscale'] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setKind(k)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
              kind === k ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900'
            }`}
          >
            {k === 'style' ? 'Styles' : 'Upscale'}
          </button>
        ))}
      </div>

      {save.error instanceof APIError && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {save.error.message}
        </div>
      )}

      <div className="mt-3 inline-flex gap-1 rounded-lg bg-ink-100 p-1">
        {(['auto', 'manual'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
              mode === m ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900'
            }`}
          >
            {m}
          </button>
        ))}
      </div>

      <div className="mt-4 rounded-lg border border-ink-100">
        {cfg.isLoading && <div className="px-3 py-3 text-center text-xs text-ink-400">Loading…</div>}
        {!cfg.isLoading && draft.length === 0 && (
          <div className="px-3 py-3 text-center text-xs text-ink-400">
            No categories yet — set a Category on a photoshoot to create a shelf.
          </div>
        )}
        {displayList.length > 0 && <HeroBoundary n={1} top />}
        {displayList.flatMap((c, i) => {
          const row = (
            <div
              key={c.name}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault()
                const sourceName = e.dataTransfer.getData('text/plain')
                if (sourceName) reorder(sourceName, c.name)
              }}
              className="flex items-center gap-3 border-b border-ink-50 px-3 py-2 last:border-b-0"
            >
              {mode === 'manual'
                ? <DragHandle id={c.name} />
                : <span className="w-[1ch] select-none text-center text-ink-200">·</span>}
              <span className="flex-1 text-sm text-ink-900">{c.name}</span>
              <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-500">{c.count}</span>
            </div>
          )
          return (i + 1) % 3 === 0
            ? [row, <HeroBoundary key={`hero-${i}`} n={(i + 1) / 3 + 1} />]
            : [row]
        })}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <p className="text-xs text-ink-400">
          {draft.length} {draft.length === 1 ? 'category' : 'categories'} → {heroSlots} hero {heroSlots === 1 ? 'slot' : 'slots'}
        </p>
        <button
          type="button"
          disabled={save.isPending || !dirty}
          onClick={() => save.mutate({ mode, order: draft.map((c) => c.name) })}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
        >
          {save.isPending ? 'Saving…' : 'Save order'}
        </button>
      </div>
      </>
      )}
    </div>
  )
}

// Hero slots (PX-461/465). Generalises the single "Most popular" pin into N
// interleaved heroes (one at the top, one after every 3 catalog shelves). The
// number of slots = floor(num_categories/3)+1, computed by the backend and
// returned as slot_limit — so adding categories grows the available slots.
function HeroSlotsCard({ photoshoots }: { photoshoots: AdminPhotoshootResponse[] }) {
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<'style' | 'upscale'>('style')
  const cfg = useQuery({
    queryKey: ['admin', 'featured', 'heroes', kind],
    queryFn: () => request<AdminHeroSlotsResponse>(`/admin/featured/heroes?kind=${kind}`),
  })
  const save = useMutation({
    mutationFn: (body: AdminHeroSlotsUpdate) =>
      request<AdminHeroSlotsResponse>(`/admin/featured/heroes?kind=${kind}`, { method: 'PATCH', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'featured', 'heroes', kind] }),
  })

  // Draft is exactly `slot_limit` rows — stored slots padded with auto slots so
  // the operator always sees every available slot (and never more than the cap).
  const padded = (data: AdminHeroSlotsResponse): AdminHeroSlot[] => {
    const out = data.slots.slice(0, data.slot_limit)
    while (out.length < data.slot_limit) out.push({ mode: 'auto', photoshoot_id: null })
    return out
  }
  const [draft, setDraft] = useState<AdminHeroSlot[]>([])
  const [expanded, setExpanded] = useState(false)
  useEffect(() => {
    if (cfg.data) setDraft(padded(cfg.data))
  }, [cfg.data])

  const setSlot = (i: number, patch: Partial<AdminHeroSlot>) =>
    setDraft((cur) => cur.map((s, idx) => (idx === i ? { ...s, ...patch } : s)))

  const pinnable = photoshoots.filter((p) => p.kind === kind)
  const dirty = cfg.data ? JSON.stringify(draft) !== JSON.stringify(padded(cfg.data)) : false

  return (
    <div className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <h2 className="text-base font-semibold tracking-tight">Hero banners</h2>
          <p className="mt-1 text-xs text-ink-400">
            Heroes shown in the iOS {kind === 'style' ? 'Styles' : 'Upscale'} feed — one at the top, one
            after every 3 catalog shelves. Auto picks Top-1 by popularity; Manual pins a photoshoot. The
            number of slots grows with the number of categories.
          </p>
        </div>
        <span className="ml-3 shrink-0 text-ink-400">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
      <>
      <div className="mt-3 inline-flex gap-1 rounded-lg bg-ink-100 p-1">
        {(['style', 'upscale'] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setKind(k)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
              kind === k ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900'
            }`}
          >
            {k === 'style' ? 'Styles' : 'Upscale'}
          </button>
        ))}
      </div>

      {save.error instanceof APIError && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {save.error.message}
        </div>
      )}

      <div className="mt-4 space-y-3">
        {cfg.isLoading && <div className="px-3 py-3 text-center text-xs text-ink-400">Loading…</div>}
        {draft.map((slot, i) => (
          <div key={i} className="rounded-lg border border-ink-100 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-ink-500">Hero {i + 1}{i === 0 ? ' (top)' : ''}</span>
              <div className="inline-flex gap-1 rounded-lg bg-ink-100 p-1">
                {(['auto', 'manual'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setSlot(i, { mode: m, photoshoot_id: m === 'auto' ? null : slot.photoshoot_id })}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
                      slot.mode === m ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900'
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            {slot.mode === 'manual' && (
              <select
                value={slot.photoshoot_id ?? ''}
                onChange={(e) => setSlot(i, { photoshoot_id: e.target.value || null })}
                className="mt-2 w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
              >
                <option value="">— select —</option>
                {pinnable.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.slug})</option>
                ))}
              </select>
            )}
          </div>
        ))}
      </div>

      <p className="mt-3 text-xs text-ink-400">Hero media (video / before-after) is set per photoshoot in its editor.</p>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          disabled={save.isPending || !dirty}
          onClick={() => save.mutate({ slots: draft })}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      </>
      )}
    </div>
  )
}

// AI-profile intro-screen preview style (PX-416). A single optional pin; the
// before/after slider on the iOS intro screen showcases this style. Unset →
// iOS falls back to the featured weekly style.
function AiProfilePreviewCard({ photoshoots }: { photoshoots: AdminPhotoshootResponse[] }) {
  const queryClient = useQueryClient()
  const cfg = useQuery({
    queryKey: ['admin', 'settings', 'ai-profile-preview'],
    queryFn: () => request<AdminAiProfilePreviewResponse>('/admin/settings/ai-profile-preview'),
  })
  const save = useMutation({
    mutationFn: (body: AdminAiProfilePreviewUpdate) =>
      request<AdminAiProfilePreviewResponse>('/admin/settings/ai-profile-preview', {
        method: 'PUT',
        body,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'ai-profile-preview'] }),
  })

  const [photoshootId, setPhotoshootId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (cfg.data) setPhotoshootId(cfg.data.photoshoot_id)
  }, [cfg.data])

  return (
    <div className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <h2 className="text-base font-semibold tracking-tight">AI profile preview</h2>
          <p className="mt-1 text-xs text-ink-400">
            Style showcased on the AI-profile intro screen (before/after slider). Leave unset to fall back to the featured weekly style.
          </p>
        </div>
        <span className="ml-3 shrink-0 text-ink-400">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
      <>
      {save.error instanceof APIError && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {save.error.message}
        </div>
      )}

      <label className="mt-4 block text-sm">
        <span className="mb-1 block text-xs font-medium text-ink-500">Photoshoot</span>
        <select
          value={photoshootId ?? ''}
          onChange={(e) => setPhotoshootId(e.target.value || null)}
          className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
        >
          <option value="">— fall back to featured —</option>
          {photoshoots.map((p) => (
            <option key={p.id} value={p.id}>{p.name} ({p.slug})</option>
          ))}
        </select>
      </label>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          disabled={save.isPending}
          onClick={() => save.mutate({ photoshoot_id: photoshootId })}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      </>
      )}
    </div>
  )
}

// AI-profile creation guidelines (PX-416): the Do/Don't example tiles shown on
// the upload-guidelines screen. Each tile = an uploaded image + a short caption
// + a Do/Don't flag. Empty list → iOS shows bundled placeholders. Stored whole
// via PUT (replace), so the editor collects locally then saves.
function AiProfileGuidelinesCard() {
  const queryClient = useQueryClient()
  const cfg = useQuery({
    queryKey: ['admin', 'settings', 'ai-profile-guidelines'],
    queryFn: () =>
      request<AdminAiProfileGuidelinesResponse>('/admin/settings/ai-profile-guidelines'),
  })
  const save = useMutation({
    mutationFn: (body: AdminAiProfileGuidelinesUpdate) =>
      request<AdminAiProfileGuidelinesResponse>('/admin/settings/ai-profile-guidelines', {
        method: 'PUT',
        body,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'ai-profile-guidelines'] }),
  })

  const [items, setItems] = useState<AiProfileGuidelineItem[]>([])
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (cfg.data) setItems(cfg.data.items)
  }, [cfg.data])

  function update(i: number, patch: Partial<AiProfileGuidelineItem>) {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))
  }
  function remove(i: number) {
    setItems((prev) => prev.filter((_, idx) => idx !== i))
  }
  function add(isGood: boolean) {
    setItems((prev) => [...prev, { image_url: '', caption: '', is_good: isGood }])
  }

  // A row is complete only with both an image and a caption; drop blanks on save.
  const cleaned = items.filter((it) => it.image_url.trim() && it.caption.trim())
  const goodCount = items.filter((it) => it.is_good).length
  const badCount = items.length - goodCount

  return (
    <div className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <h2 className="text-base font-semibold tracking-tight">AI profile guidelines</h2>
          <p className="mt-1 text-xs text-ink-400">
            Do / Don&apos;t example photos on the AI-profile creation screen. {items.length === 0 ? 'Using built-in placeholders.' : `${goodCount} good · ${badCount} bad`}
          </p>
        </div>
        <span className="ml-3 shrink-0 text-ink-400">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
      <>
      {save.error instanceof APIError && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {save.error.message}
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {items.map((it, i) => (
          <div key={i} className="rounded-lg border border-ink-100 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="inline-flex gap-1 rounded-lg bg-ink-100 p-1">
                {([true, false] as const).map((g) => (
                  <button
                    key={String(g)}
                    type="button"
                    onClick={() => update(i, { is_good: g })}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
                      it.is_good === g ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900'
                    }`}
                  >
                    {g ? 'Do' : "Don't"}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-xs font-medium text-red-600 hover:text-red-700"
              >
                Remove
              </button>
            </div>
            <MediaDropzone
              value={it.image_url}
              onChange={(url) => update(i, { image_url: url })}
              accept="image"
            />
            <input
              type="text"
              value={it.caption}
              maxLength={40}
              placeholder="Caption (e.g. Selfie)"
              onChange={(e) => update(i, { caption: e.target.value })}
              className="mt-2 w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => add(true)}
          className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50"
        >
          + Do example
        </button>
        <button
          type="button"
          onClick={() => add(false)}
          className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50"
        >
          + Don&apos;t example
        </button>
        <span className="text-xs text-ink-400">{goodCount} good · {badCount} bad</span>
        <div className="ml-auto">
          <button
            type="button"
            disabled={save.isPending}
            onClick={() => save.mutate({ items: cleaned })}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      </>
      )}
    </div>
  )
}

// v2 onboarding funnel rollout flags (PX-439/475). `enabled` gates the funnel on
// cold start (= first_run_funnel_v2_enabled); `demo` is the App-Review hook
// (forces it on for reviewers even when enabled is off). Replaces editing the
// `funnel_v2` app_settings row by hand in the DB.
function FunnelV2Card() {
  const queryClient = useQueryClient()
  const cfg = useQuery({
    queryKey: ['admin', 'settings', 'funnel-v2'],
    queryFn: () => request<AdminFunnelV2Response>('/admin/settings/funnel-v2'),
  })
  const save = useMutation({
    mutationFn: (body: AdminFunnelV2Update) =>
      request<AdminFunnelV2Response>('/admin/settings/funnel-v2', { method: 'PUT', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'funnel-v2'] }),
  })

  const [enabled, setEnabled] = useState(false)
  const [demo, setDemo] = useState(false)
  useEffect(() => {
    if (cfg.data) {
      setEnabled(cfg.data.enabled)
      setDemo(cfg.data.demo)
    }
  }, [cfg.data])

  const dirty = !!cfg.data && (enabled !== cfg.data.enabled || demo !== cfg.data.demo)

  return (
    <div className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
      <h2 className="text-base font-semibold tracking-tight">v2 onboarding funnel</h2>
      <p className="mt-1 text-xs text-ink-400">
        Rollout flags for the first-run funnel. <span className="font-medium">Enabled</span> shows it to
        every signed-out first-opener; <span className="font-medium">Demo</span> forces it on for App
        Review even when Enabled is off. Needs ≥1 onboarding filter below to be useful.
      </p>

      {save.error instanceof APIError && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {save.error.message}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-6">
        <Toggle label="Enabled" checked={enabled} onChange={setEnabled} disabled={cfg.isLoading} />
        <Toggle label="Demo (App Review)" checked={demo} onChange={setDemo} disabled={cfg.isLoading} />
        <div className="ml-auto">
          <button
            type="button"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate({ enabled, demo })}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Onboarding filters (PX-440): up to 4 "ad filter" tiles shown on the new
// onboarding funnel first screen. Each tile references a photoshoot + carries
// a before/after image pair. sort_order is derived from position in the array.
// Stored whole via PUT (replace), same pattern as AiProfileGuidelinesCard.
const MAX_ONBOARDING_FILTERS = 4

function OnboardingFiltersCard({ photoshoots }: { photoshoots: AdminPhotoshootResponse[] }) {
  const queryClient = useQueryClient()
  const cfg = useQuery({
    queryKey: ['admin', 'settings', 'onboarding-filters'],
    queryFn: () =>
      request<AdminOnboardingFiltersResponse>('/admin/settings/onboarding-filters'),
  })
  const save = useMutation({
    mutationFn: (body: AdminOnboardingFiltersUpdate) =>
      request<AdminOnboardingFiltersResponse>('/admin/settings/onboarding-filters', {
        method: 'PUT',
        body,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'onboarding-filters'] }),
  })

  const [items, setItems] = useState<OnboardingFilterItem[]>([])
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (cfg.data) setItems(cfg.data.items)
  }, [cfg.data])

  function update(i: number, patch: Partial<OnboardingFilterItem>) {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))
  }
  function remove(i: number) {
    setItems((prev) => prev.filter((_, idx) => idx !== i))
  }
  function add() {
    if (items.length >= MAX_ONBOARDING_FILTERS) return
    setItems((prev) => [
      ...prev,
      { photoshoot_id: '', before_image_url: '', after_image_url: '', sort_order: prev.length },
    ])
  }

  // A row is complete only when all three required fields are filled;
  // also assign sort_order = index position before saving.
  const isComplete = (it: OnboardingFilterItem) =>
    !!it.photoshoot_id && !!it.before_image_url.trim() && !!it.after_image_url.trim()
  const cleaned = items.filter(isComplete).map((it, idx) => ({ ...it, sort_order: idx }))
  // A row the operator has started (any field touched) but not finished —
  // these are silently dropped from the save, so warn instead of failing quietly.
  const isIncomplete = (it: OnboardingFilterItem) =>
    (!!it.photoshoot_id || !!it.before_image_url.trim() || !!it.after_image_url.trim()) && !isComplete(it)
  const incompleteCount = items.filter(isIncomplete).length

  return (
    <div className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <h2 className="text-base font-semibold tracking-tight">Onboarding filters</h2>
          <p className="mt-1 text-xs text-ink-400">
            Up to {MAX_ONBOARDING_FILTERS} promotional &ldquo;ad filter&rdquo; tiles on the onboarding first screen.{' '}
            {items.length === 0
              ? 'None configured — the funnel’s first screen will be empty (iOS has no built-in defaults).'
              : `${items.length} filter${items.length !== 1 ? 's' : ''} configured.`}
          </p>
        </div>
        <span className="ml-3 shrink-0 text-ink-400">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <>
          {save.error instanceof APIError && (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {save.error.message}
            </div>
          )}

          {incompleteCount > 0 && (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              {incompleteCount} filter{incompleteCount !== 1 ? 's' : ''} won&rsquo;t be saved — each needs a
              photoshoot <span className="font-semibold">and</span> both a before and after image. Incomplete
              rows are highlighted below.
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {items.map((it, i) => (
              <div
                key={i}
                className={`rounded-lg border p-4 ${
                  isIncomplete(it) ? 'border-amber-300 bg-amber-50/40' : 'border-ink-100'
                }`}
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-semibold text-ink-500">Filter {i + 1}</span>
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    className="text-xs font-medium text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>

                {/* Photoshoot picker */}
                <label className="block text-sm">
                  <span className="mb-1 block text-xs font-medium text-ink-500">Photoshoot</span>
                  <select
                    value={it.photoshoot_id}
                    onChange={(e) => {
                      const id = e.target.value
                      const ps = photoshoots.find((p) => p.id === id)
                      // Auto-fill before/after from the chosen photoshoot's own
                      // reveal pair (still editable — the operator can upload a
                      // custom ad creative after). Always reflect the SELECTED
                      // photoshoot: if it has no pair (or selection cleared), blank
                      // the fields so a stale image from a previous pick can't be
                      // mismatched against the new style (the row then shows as
                      // incomplete + highlights amber).
                      update(i, {
                        photoshoot_id: id,
                        before_image_url: ps?.before_image_url ?? '',
                        after_image_url: ps?.after_image_url ?? '',
                      })
                    }}
                    className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  >
                    <option value="">— select a photoshoot —</option>
                    {photoshoots.map((ps) => (
                      <option key={ps.id} value={ps.id}>
                        {ps.name}
                        {ps.kind !== 'style' ? ` (${ps.kind})` : ''}
                      </option>
                    ))}
                  </select>
                </label>

                {/* Before — image or video (PX-479) */}
                <div className="mt-3">
                  <span className="mb-1 block text-xs font-medium text-ink-500">Before (image or video)</span>
                  <MediaDropzone
                    value={it.before_image_url}
                    onChange={(url) => update(i, { before_image_url: url })}
                    accept="media"
                  />
                </div>

                {/* After — image or video (PX-479) */}
                <div className="mt-3">
                  <span className="mb-1 block text-xs font-medium text-ink-500">After (image or video)</span>
                  <MediaDropzone
                    value={it.after_image_url}
                    onChange={(url) => update(i, { after_image_url: url })}
                    accept="media"
                  />
                  <AfterVideoWarning before={it.before_image_url} after={it.after_image_url} />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={items.length >= MAX_ONBOARDING_FILTERS}
              onClick={add}
              className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              + Add filter
            </button>
            <span className="text-xs text-ink-400">
              {items.length} / {MAX_ONBOARDING_FILTERS}
            </span>
            <div className="ml-auto">
              <button
                type="button"
                disabled={save.isPending}
                onClick={() => save.mutate({ items: cleaned })}
                className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function TrendingCard({ photoshoots }: { photoshoots: AdminPhotoshootResponse[] }) {
  const queryClient = useQueryClient()
  // PX-447: per-kind Trends config — Styles vs Upscale each have their own
  // mode/limit (and their own pinned set via the per-card flag + kind).
  const [kind, setKind] = useState<'style' | 'upscale'>('style')
  const cfg = useQuery({
    queryKey: ['admin', 'featured', 'trending', kind],
    queryFn: () => request<AdminTrendingShelfResponse>(`/admin/featured/trending?kind=${kind}`),
  })
  const save = useMutation({
    mutationFn: (body: AdminTrendingShelfUpdate) =>
      request<AdminTrendingShelfResponse>(`/admin/featured/trending?kind=${kind}`, { method: 'PATCH', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'featured', 'trending', kind] }),
  })

  const [mode, setMode] = useState<'auto' | 'manual'>('auto')
  const [limit, setLimit] = useState(8)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (cfg.data) {
      setMode(cfg.data.mode)
      setLimit(cfg.data.limit)
    }
  }, [cfg.data])

  // Manual-mode pinned set is scoped to the selected kind (the public trending
  // query filters by kind too).
  const pinned = useMemo(
    () => photoshoots.filter((p) => p.is_trending_pinned && p.kind === kind),
    [photoshoots, kind],
  )

  return (
    <div className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <h2 className="text-base font-semibold tracking-tight">Trends shelf</h2>
          <p className="mt-1 text-xs text-ink-400">
            Auto = Top-N by 7-day count. Manual = every photoshoot with the Trending flag (toggle on the card below). Styles and Upscale have separate configs.
          </p>
        </div>
        <span className="ml-3 shrink-0 text-ink-400">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
      <>
      <div className="mt-3 inline-flex gap-1 rounded-lg bg-ink-100 p-1">
        {(['style', 'upscale'] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setKind(k)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
              kind === k ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900'
            }`}
          >
            {k === 'style' ? 'Styles' : 'Upscale'}
          </button>
        ))}
      </div>

      {save.error instanceof APIError && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {save.error.message}
        </div>
      )}

      <div className="mt-4 inline-flex gap-1 rounded-lg bg-ink-100 p-1">
        {(['auto', 'manual'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
              mode === m ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900'
            }`}
          >
            {m}
          </button>
        ))}
      </div>

      <label className="mt-4 block text-sm">
        <span className="mb-1 block text-xs font-medium text-ink-500">Limit (1–20)</span>
        <input
          type="number"
          min={1}
          max={20}
          value={limit}
          onChange={(e) => setLimit(Math.max(1, Math.min(20, Number(e.target.value) || 8)))}
          className="w-24 rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
        />
      </label>

      {mode === 'manual' && (
        <div className="mt-4 rounded-md border border-ink-100 bg-ink-50 p-3 text-xs">
          <div className="mb-1 font-medium text-ink-700">Currently pinned ({pinned.length})</div>
          {pinned.length === 0 ? (
            <div className="text-ink-400">No photoshoots flagged. Toggle Trending on a card below.</div>
          ) : (
            <ul className="space-y-0.5">
              {pinned.map((p) => (
                <li key={p.id} className="font-mono text-ink-500">{p.slug}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          disabled={save.isPending}
          onClick={() => save.mutate({ mode, limit })}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Editor modal — full CRUD form.
// ---------------------------------------------------------------------------

interface EditorProps {
  mode: 'create' | 'edit'
  initial: AdminPhotoshootResponse | null
  // Distinct categories already in use — feeds the Category datalist so an
  // admin reuses an existing shelf instead of fragmenting it with a typo.
  allCategories: string[]
  onClose: () => void
}

// PX-479: before/after may be a video. The reveal slider caps the "after" clip
// to the "before" clip's duration on the app (playback-cap "trim" — no server
// re-encode), so warn the admin here when the after video is longer.
const VIDEO_URL_RE = /\.(mp4|mov|m4v|webm)(\?|$)/i
const isVideoUrl = (u: string) => VIDEO_URL_RE.test(u.trim())

function loadVideoDuration(url: string): Promise<number | null> {
  return new Promise((resolve) => {
    const v = document.createElement('video')
    v.preload = 'metadata'
    v.onloadedmetadata = () => resolve(Number.isFinite(v.duration) ? v.duration : null)
    v.onerror = () => resolve(null)
    v.src = url
  })
}

/** Warning string when both halves are videos and the after clip outlasts the
 *  before clip (it'll be capped on the app); null otherwise. */
function useAfterVideoDurationWarning(beforeUrl: string, afterUrl: string): string | null {
  const [warning, setWarning] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    const before = beforeUrl.trim()
    const after = afterUrl.trim()
    if (!isVideoUrl(before) || !isVideoUrl(after)) {
      setWarning(null)
      return
    }
    Promise.all([loadVideoDuration(before), loadVideoDuration(after)]).then(([bd, ad]) => {
      if (cancelled) return
      if (bd != null && ad != null && ad > bd + 0.1) {
        setWarning(
          `After video (${ad.toFixed(1)}s) is longer than before (${bd.toFixed(1)}s) — ` +
            `it will be capped to ${bd.toFixed(1)}s on the app.`,
        )
      } else {
        setWarning(null)
      }
    })
    return () => {
      cancelled = true
    }
  }, [beforeUrl, afterUrl])
  return warning
}

/** Amber note when the after video outlasts the before video (capped on the
 *  app). A component so it can be used inside a `.map` (hooks can't). */
function AfterVideoWarning({ before, after }: { before: string; after: string }) {
  const warning = useAfterVideoDurationWarning(before, after)
  if (!warning) return null
  return <p className="mt-1 text-xs text-amber-600">{warning}</p>
}

function PhotoshootEditorModal({ mode, initial, allCategories, onClose }: EditorProps) {
  const queryClient = useQueryClient()
  const models = useQuery({
    queryKey: ['admin', 'catalog', 'models'],
    queryFn: () => request<AdminModelResponse[]>('/admin/catalog/models'),
  })
  const plans = useQuery({
    queryKey: ['admin', 'catalog', 'plans'],
    queryFn: () => request<AdminPlanResponse[]>('/admin/catalog/plans'),
  })

  const [name, setName] = useState(initial?.name ?? '')
  const [slug, setSlug] = useState(initial?.slug ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [coverUrl, setCoverUrl] = useState(initial?.cover_image_url ?? '')
  // Optional Style-of-the-Week hero media (PX-261). '' = cleared → null on save.
  const [videoUrl, setVideoUrl] = useState(initial?.video_url ?? '')
  const [beforeUrl, setBeforeUrl] = useState(initial?.before_image_url ?? '')
  const [afterUrl, setAfterUrl] = useState(initial?.after_image_url ?? '')
  const [promptTemplate, setPromptTemplate] = useState(initial?.prompt_template ?? '')
  const [category, setCategory] = useState(initial?.category ?? '')
  const [tagsRaw, setTagsRaw] = useState(initial?.tags.join(', ') ?? '')
  const [allowedPlanIds, setAllowedPlanIds] = useState<number[]>(initial?.allowed_plan_ids ?? [1, 2])
  const [allowedAspectRatios, setAllowedAspectRatios] = useState<string[]>(initial?.allowed_aspect_ratios ?? [])
  const [customRatio, setCustomRatio] = useState('')
  // Photo-upload policy (PX-402). Mode is DERIVED from photo_slots on load:
  // non-empty → SLOT mode, else COUNT. Both states are kept so toggling back and
  // forth doesn't lose the other mode's draft; the body sent picks by mode.
  const [photoUploadMode, setPhotoUploadMode] = useState<'count' | 'slots'>(
    (initial?.photo_slots?.length ?? 0) > 0 ? 'slots' : 'count',
  )
  const [photoCountMin, setPhotoCountMin] = useState(initial?.photo_count_min ?? 1)
  const [photoCountMax, setPhotoCountMax] = useState(initial?.photo_count_max ?? 3)
  const [photoSlots, setPhotoSlots] = useState<PhotoSlot[]>(initial?.photo_slots ?? [])
  const [newSlotLabel, setNewSlotLabel] = useState('')
  const [sortOrder, setSortOrder] = useState(initial?.sort_order ?? 0)
  const [isActive, setIsActive] = useState(initial?.is_active ?? true)
  const [isTrendingPinned, setIsTrendingPinned] = useState(initial?.is_trending_pinned ?? false)
  // PX-437: destination tab — Styles vs the new Upscale tab.
  const [kind, setKind] = useState<'style' | 'upscale'>(initial?.kind ?? 'style')

  const [deleteConfirm, setDeleteConfirm] = useState(false)

  const slugInvalid = !!slug && !SLUG_RE.test(slug)
  // PX-277: the "AI model" field is gone — `model_id` (NOT NULL FK, still the
  // generation default/fallback) is no longer hand-picked. On EDIT it keeps its
  // existing value (never re-sent); on CREATE it's auto-set to the first active
  // model. Models for the user are managed solely via Model variants.
  const createModelId = initial?.model_id ?? models.data?.find((m) => m.is_active)?.id ?? ''

  // Photo-upload policy (PX-402). Validation mirrors the backend admin schema:
  // count 1≤min≤max≤10; slots 1..10, label 1..40 chars, non-blank, unique.
  const photoCountValid = photoCountMin >= 1 && photoCountMax <= 10 && photoCountMin <= photoCountMax
  const slotLabels = photoSlots.map((s) => s.label.trim())
  const photoSlotsValid =
    photoSlots.length >= 1 &&
    photoSlots.length <= 10 &&
    slotLabels.every((l) => l.length >= 1 && l.length <= 40) &&
    new Set(slotLabels).size === slotLabels.length
  const photoUploadValid = photoUploadMode === 'count' ? photoCountValid : photoSlotsValid
  // The slots actually persisted: empty in COUNT mode (→ backend uses min/max).
  const effectivePhotoSlots: PhotoSlot[] = photoUploadMode === 'slots' ? photoSlots : []

  const canSave =
    !!name && !!slug && !slugInvalid && !!coverUrl && !!promptTemplate && !!createModelId && photoUploadValid

  const tags = useMemo(
    () => tagsRaw.split(',').map((s) => s.trim()).filter(Boolean),
    [tagsRaw],
  )

  const createMutation = useMutation({
    mutationFn: (body: AdminPhotoshootCreate) =>
      request<AdminPhotoshootResponse>('/admin/photoshoots', { method: 'POST', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'photoshoots'] })
      onClose()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: AdminPhotoshootUpdate }) =>
      request<AdminPhotoshootResponse>(`/admin/photoshoots/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'photoshoots'] })
      onClose()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      request<void>(`/admin/photoshoots/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'photoshoots'] })
      onClose()
    },
  })

  const handleSave = () => {
    if (!canSave) return
    if (mode === 'create') {
      const body: AdminPhotoshootCreate = {
        name,
        slug,
        description: description.trim() || null,
        cover_image_url: coverUrl,
        video_url: videoUrl.trim() || null,
        before_image_url: beforeUrl.trim() || null,
        after_image_url: afterUrl.trim() || null,
        category: category.trim() || null,
        tags,
        prompt_template: promptTemplate,
        model_id: createModelId,
        allowed_plan_ids: allowedPlanIds,
        // Upscale styles (PX-516) carry no aspect-ratio policy.
        allowed_aspect_ratios: kind === 'upscale' ? [] : allowedAspectRatios,
        photo_count_min: photoCountMin,
        photo_count_max: photoCountMax,
        photo_slots: effectivePhotoSlots,
        is_active: isActive,
        is_trending_pinned: isTrendingPinned,
        sort_order: sortOrder,
        kind,
      }
      createMutation.mutate(body)
    } else if (initial) {
      // PATCH only changed fields — minimal diff vs initial.
      const body: AdminPhotoshootUpdate = {}
      if (name !== initial.name) body.name = name
      if (slug !== initial.slug) body.slug = slug
      const trimmedDesc = description.trim() || null
      if (trimmedDesc !== initial.description) body.description = trimmedDesc
      if (coverUrl !== initial.cover_image_url) body.cover_image_url = coverUrl
      // Hero media — '' maps to null (cleared); send only when changed.
      const nextVideo = videoUrl.trim() || null
      if (nextVideo !== (initial.video_url ?? null)) body.video_url = nextVideo
      const nextBefore = beforeUrl.trim() || null
      if (nextBefore !== (initial.before_image_url ?? null)) body.before_image_url = nextBefore
      const nextAfter = afterUrl.trim() || null
      if (nextAfter !== (initial.after_image_url ?? null)) body.after_image_url = nextAfter
      if (promptTemplate !== initial.prompt_template) body.prompt_template = promptTemplate
      const nextCategory = category.trim() || null
      if (nextCategory !== (initial.category ?? null)) body.category = nextCategory
      if (JSON.stringify(tags) !== JSON.stringify(initial.tags)) body.tags = tags
      // model_id intentionally not editable anymore (PX-277) — kept as-is.
      if (JSON.stringify(allowedPlanIds) !== JSON.stringify(initial.allowed_plan_ids)) body.allowed_plan_ids = allowedPlanIds
      // Upscale styles (PX-516) carry no aspect-ratio policy — force [].
      const nextAspectRatios = kind === 'upscale' ? [] : allowedAspectRatios
      if (JSON.stringify(nextAspectRatios) !== JSON.stringify(initial.allowed_aspect_ratios ?? [])) body.allowed_aspect_ratios = nextAspectRatios
      // Photo-upload policy. Send min+max together when EITHER changed (the
      // backend update-validator checks min<=max only when both are present).
      if (photoCountMin !== initial.photo_count_min || photoCountMax !== initial.photo_count_max) {
        body.photo_count_min = photoCountMin
        body.photo_count_max = photoCountMax
      }
      if (JSON.stringify(effectivePhotoSlots) !== JSON.stringify(initial.photo_slots ?? [])) {
        body.photo_slots = effectivePhotoSlots
      }
      if (sortOrder !== initial.sort_order) body.sort_order = sortOrder
      if (isActive !== initial.is_active) body.is_active = isActive
      if (isTrendingPinned !== initial.is_trending_pinned) body.is_trending_pinned = isTrendingPinned
      if (kind !== initial.kind) body.kind = kind
      updateMutation.mutate({ id: initial.id, body })
    }
  }

  const handleDelete = () => {
    if (!initial) return
    if (!deleteConfirm) {
      setDeleteConfirm(true)
      return
    }
    deleteMutation.mutate(initial.id)
  }

  const togglePlan = (planId: number) => {
    setAllowedPlanIds((prev) =>
      prev.includes(planId) ? prev.filter((id) => id !== planId) : [...prev, planId].sort((a, b) => a - b),
    )
  }

  const toggleRatio = (ratio: string) => {
    setAllowedAspectRatios((prev) =>
      prev.includes(ratio) ? prev.filter((r) => r !== ratio) : [...prev, ratio],
    )
  }

  const customRatioValid = ASPECT_RE.test(customRatio) && !allowedAspectRatios.includes(customRatio)
  const addCustomRatio = () => {
    if (!customRatioValid) return
    setAllowedAspectRatios((prev) => [...prev, customRatio])
    setCustomRatio('')
  }

  const newSlotLabelTrimmed = newSlotLabel.trim()
  const newSlotValid =
    newSlotLabelTrimmed.length >= 1 &&
    newSlotLabelTrimmed.length <= 40 &&
    photoSlots.length < 10 &&
    !slotLabels.includes(newSlotLabelTrimmed)
  const addSlot = () => {
    if (!newSlotValid) return
    setPhotoSlots((prev) => [...prev, { label: newSlotLabelTrimmed, media_kind: 'photo' }])
    setNewSlotLabel('')
  }
  const removeSlot = (index: number) => {
    setPhotoSlots((prev) => prev.filter((_, i) => i !== index))
  }
  // PX-479: flip a slot between collecting a photo and a motion video.
  const setSlotMediaKind = (index: number, mediaKind: 'photo' | 'video') => {
    setPhotoSlots((prev) =>
      prev.map((s, i) => (i === index ? { ...s, media_kind: mediaKind } : s)),
    )
  }

  const error =
    createMutation.error instanceof APIError ? createMutation.error :
    updateMutation.error instanceof APIError ? updateMutation.error :
    deleteMutation.error instanceof APIError ? deleteMutation.error :
    null

  const isPending = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-6">
      <div className="my-4 w-full max-w-2xl rounded-xl bg-white shadow-xl">
        <header className="flex items-start justify-between gap-3 border-b border-ink-100 p-5">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              {mode === 'create' ? 'New photoshoot' : `Edit · ${initial?.name}`}
            </h2>
            {mode === 'edit' && (
              <p className="mt-0.5 font-mono text-[11px] text-ink-400">{initial?.id}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ink-100 px-2 py-1 text-xs text-ink-500 transition hover:border-ink-200 hover:text-ink-900"
          >
            ✕ Close
          </button>
        </header>

        {error && (
          <div className="m-5 mb-0 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error.message}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
          <Field label="Name" required>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </Field>

          <Field label="Slug" required hint="lowercase, digits, dashes only">
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className={`w-full rounded-md border px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 ${
                slugInvalid
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                  : 'border-ink-200 focus:border-brand-500 focus:ring-brand-100'
              }`}
            />
            {slugInvalid && (
              <span className="mt-1 block text-[11px] text-red-600">Must match {String(SLUG_RE)}</span>
            )}
          </Field>

          <SectionDivider />

          <Field label="Cover image" required className="sm:col-span-2" asDiv>
            <MediaDropzone value={coverUrl} onChange={setCoverUrl} />
          </Field>

          <SectionDivider />

          {/* Optional Style-of-the-Week hero media (PX-261). iOS render
              priority: video → before/after pair → cover. Clear a slot to
              fall through to the next. */}
          <div className="space-y-4 rounded-lg border border-ink-100 bg-ink-50/60 p-4 sm:col-span-2">
            <p className="text-xs text-ink-500">
              Hero media for the <strong>Style of the Week</strong> banner (optional).
              Priority on the app: <strong>video</strong> → <strong>before/after pair</strong> → cover.
              Clear a slot to fall through. Before/after each accept an image OR a video.
            </p>
            <Field label="Video (mp4/mov, looped)" asDiv>
              <MediaDropzone value={videoUrl} onChange={setVideoUrl} accept="media" />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Before (image or video)" asDiv>
                <MediaDropzone value={beforeUrl} onChange={setBeforeUrl} accept="media" />
              </Field>
              <Field label="After (image or video)" asDiv>
                <MediaDropzone value={afterUrl} onChange={setAfterUrl} accept="media" />
              </Field>
            </div>
            {(beforeUrl.trim() === '') !== (afterUrl.trim() === '') && (
              <p className="text-xs text-amber-600">
                The before/after reveal needs both halves — one alone is ignored on the app.
              </p>
            )}
            <AfterVideoWarning before={beforeUrl} after={afterUrl} />
          </div>

          <SectionDivider />

          {/* Model variants sit above Description (PX-314) — model choice before
              the text fields. Custom variables stay under Prompt template (PX-311). */}
          {mode === 'edit' && initial ? (
            <div className="sm:col-span-2">
              <VariantsSection photoshootId={initial.id} defaultModelId={initial.model_id} models={models.data ?? []} />
            </div>
          ) : (
            <Field label="Model variants" className="sm:col-span-2">
              <span className="text-xs text-ink-400">Save the photoshoot first, then reopen it to attach extra models.</span>
            </Field>
          )}

          <SectionDivider />

          <Field label="Description" className="sm:col-span-2">
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </Field>

          <SectionDivider />

          <Field label="Prompt template" required className="sm:col-span-2" hint="Sent to fal.ai. Supports model-specific placeholders.">
            <textarea
              rows={4}
              value={promptTemplate}
              onChange={(e) => setPromptTemplate(e.target.value)}
              className="w-full rounded-md border border-ink-200 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </Field>

          <SectionDivider />

          {/* Custom variables sit directly under the prompt template (PX-311) so
              the {key} placeholders and the variables that fill them stay in view
              together. Model variants (extra models) live lower — unrelated to {key}. */}
          {mode === 'edit' && initial ? (
            <div className="sm:col-span-2">
              <VariablesSection photoshootId={initial.id} />
            </div>
          ) : (
            <Field label="Custom variables" className="sm:col-span-2">
              <span className="text-xs text-ink-400">Save the photoshoot first, then reopen it to add custom variables.</span>
            </Field>
          )}

          <SectionDivider />

          <Field label="Category" hint="One shelf on the iOS Styles tab. Empty = uncategorised (no shelf). Reuse an existing one to avoid splitting shelves.">
            <input
              type="text"
              list="photoshoot-categories"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Aesthetic"
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
            <datalist id="photoshoot-categories">
              {allCategories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Field>

          <Field label="Tags" hint="Comma-separated. Descriptive labels shown on the card + hero — NOT used for shelves.">
            <input
              type="text"
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value)}
              placeholder="portrait, cinematic"
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
            {tags.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {tags.map((t) => (
                  <span key={t} className="rounded bg-ink-100 px-1.5 py-0.5 text-[11px] text-ink-700">{t}</span>
                ))}
              </div>
            )}
          </Field>

          <SectionDivider />

          <Field label="Sort order" hint="Lower = earlier in lists.">
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </Field>

          <Field label="Destination" hint="Which app tab this style lives in. Styles = the regular feed; Upscale = the dedicated Upscale tab.">
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as 'style' | 'upscale')}
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            >
              <option value="style">Styles</option>
              <option value="upscale">Upscale</option>
            </select>
          </Field>

          <SectionDivider />

          <Field label="Allowed plans" required className="sm:col-span-2" hint="Empty = visible to everyone. Otherwise rank-semantics: any plan with rank ≥ min(selected ranks) passes.">
            <PlanChips
              plans={plans.data ?? []}
              selectedIds={allowedPlanIds}
              onToggle={togglePlan}
            />
          </Field>

          {/* Upscale styles (PX-516) have no aspect-ratio choice — hide Formats. */}
          {kind !== 'upscale' && (
          <>
          <SectionDivider />

          <Field label="Formats" required className="sm:col-span-2" hint="Empty = all standard ratios selectable in-app. Pick a subset to limit choice; one to lock. Custom like 2:3 allowed.">
            <div className="flex flex-wrap items-center gap-2">
              {STANDARD_RATIOS.map((r) => {
                const checked = allowedAspectRatios.includes(r)
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => toggleRatio(r)}
                    className={`rounded-md border px-3 py-1.5 font-mono text-xs font-medium transition ${
                      checked
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-ink-200 bg-white text-ink-500 hover:border-ink-300'
                    }`}
                  >
                    {r}
                  </button>
                )
              })}
              {allowedAspectRatios.filter((r) => !STANDARD_RATIOS.includes(r)).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => toggleRatio(r)}
                  className="inline-flex items-center gap-1 rounded-md border border-brand-500 bg-brand-50 px-3 py-1.5 font-mono text-xs font-medium text-brand-700 transition hover:border-brand-600"
                  title="Custom ratio — click to remove"
                >
                  {r} <span className="text-brand-400">✕</span>
                </button>
              ))}
              <input
                type="text"
                value={customRatio}
                onChange={(e) => setCustomRatio(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomRatio() } }}
                placeholder="2:3"
                className="w-20 rounded-md border border-ink-200 px-2 py-1.5 font-mono text-xs focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
              />
              <button
                type="button"
                onClick={addCustomRatio}
                disabled={!customRatioValid}
                className="rounded-md border border-ink-200 px-2 py-1.5 text-xs font-medium text-brand-600 transition hover:border-brand-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                + add
              </button>
            </div>
            <span className="mt-1 block text-[11px] text-ink-400">
              {allowedAspectRatios.length === 0
                ? 'All standard ratios selectable in-app.'
                : `Locked to: ${allowedAspectRatios.join(', ')}`}
            </span>
          </Field>
          </>
          )}

          <SectionDivider />

          <Field
            label="Photo upload"
            required
            className="sm:col-span-2"
            asDiv
            hint="How many photos the user uploads for this style. Count range, or named slots (each a required separate upload)."
          >
            <div className="inline-flex gap-1 rounded-lg bg-ink-100 p-1">
              {(['count', 'slots'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setPhotoUploadMode(m)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                    photoUploadMode === m ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900'
                  }`}
                >
                  {m === 'count' ? 'Count range' : 'Labeled slots'}
                </button>
              ))}
            </div>

            {photoUploadMode === 'count' ? (
              <div className="mt-3">
                <div className="flex flex-wrap items-center gap-2 text-sm text-ink-600">
                  <span>From</span>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={photoCountMin}
                    onChange={(e) => setPhotoCountMin(Number(e.target.value))}
                    className="w-16 rounded-md border border-ink-200 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  />
                  <span>to</span>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={photoCountMax}
                    onChange={(e) => setPhotoCountMax(Number(e.target.value))}
                    className="w-16 rounded-md border border-ink-200 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  />
                  <span>photos</span>
                </div>
                <span className="mt-1 block text-[11px] text-ink-400">
                  {photoCountValid
                    ? photoCountMin === photoCountMax
                      ? `User uploads exactly ${photoCountMin} photo${photoCountMin === 1 ? '' : 's'}.`
                      : `User uploads ${photoCountMin}–${photoCountMax} photos.`
                    : ''}
                </span>
                {!photoCountValid && (
                  <span className="mt-1 block text-[11px] font-medium text-red-500">
                    Require 1 ≤ min ≤ max ≤ 10.
                  </span>
                )}
              </div>
            ) : (
              <div className="mt-3">
                {photoSlots.length > 0 && (
                  <div className="mb-2 flex flex-col gap-1.5">
                    {photoSlots.map((slot, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 rounded-md border border-ink-200 bg-white px-2.5 py-1.5"
                      >
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-ink-100 text-[11px] font-medium text-ink-500">
                          {i + 1}
                        </span>
                        <span className="flex-1 truncate text-sm text-ink-900">{slot.label}</span>
                        <div className="flex shrink-0 overflow-hidden rounded-md border border-ink-200">
                          {(['photo', 'video'] as const).map((mk) => {
                            const active = (slot.media_kind ?? 'photo') === mk
                            return (
                              <button
                                key={mk}
                                type="button"
                                onClick={() => setSlotMediaKind(i, mk)}
                                className={
                                  'px-2 py-0.5 text-[11px] font-medium transition ' +
                                  (active
                                    ? 'bg-brand-500 text-white'
                                    : 'bg-white text-ink-500 hover:bg-ink-50')
                                }
                                title={mk === 'photo' ? 'Slot collects a photo' : 'Slot collects a motion video'}
                              >
                                {mk === 'photo' ? 'Photo' : 'Video'}
                              </button>
                            )
                          })}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSlot(i)}
                          className="text-xs font-medium text-red-500 transition hover:text-red-600"
                          title="Remove slot"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newSlotLabel}
                    onChange={(e) => setNewSlotLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addSlot()
                      }
                    }}
                    placeholder="e.g. Yourself, Your pet"
                    maxLength={40}
                    className="flex-1 rounded-md border border-ink-200 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  />
                  <button
                    type="button"
                    onClick={addSlot}
                    disabled={!newSlotValid}
                    className="rounded-md border border-ink-200 px-2.5 py-1.5 text-xs font-medium text-brand-600 transition hover:border-brand-300 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    + add slot
                  </button>
                </div>
                <span className="mt-1 block text-[11px] text-ink-400">
                  {photoSlots.length === 0
                    ? 'Add at least one slot. Each slot is a required separate upload (photo or video).'
                    : `${photoSlots.length} required slot${photoSlots.length === 1 ? '' : 's'} — user uploads one photo or video per slot.`}
                </span>
              </div>
            )}
          </Field>

          <SectionDivider />

          <Field label="Flags" className="sm:col-span-2">
            <div className="flex gap-6">
              <Toggle label="Active" checked={isActive} onChange={setIsActive} />
              <Toggle label="Trending pinned" checked={isTrendingPinned} onChange={setIsTrendingPinned} />
            </div>
          </Field>

        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-ink-100 p-5">
          <div>
            {mode === 'edit' && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isPending}
                className={`rounded-md border px-3 py-1.5 text-sm font-semibold transition disabled:opacity-60 ${
                  deleteConfirm
                    ? 'border-red-500 bg-red-500 text-white hover:bg-red-600'
                    : 'border-red-300 text-red-600 hover:bg-red-50'
                }`}
              >
                {deleteConfirm ? 'Click again to confirm delete' : 'Delete'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave || isPending}
              className="rounded-md bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
            >
              {isPending ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}

// Model variants (PX-183). A photoshoot offers extra models beyond its
// default `model_id`; the user picks one in-app. Lives only in edit mode
// (needs a persisted photoshoot id). Model cost / plan-gate are read from the
// linked model, never duplicated on the variant.
function VariantsSection({
  photoshootId,
  defaultModelId,
  models,
}: {
  photoshootId: string
  defaultModelId: string
  models: AdminModelResponse[]
}) {
  const queryClient = useQueryClient()
  const qk = ['admin', 'photoshoots', photoshootId, 'variants']

  const variantsQuery = useQuery({
    queryKey: qk,
    queryFn: () => request<AdminPhotoshootVariantResponse[]>(`/admin/photoshoots/${photoshootId}/variants`),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: qk })

  const addVariant = useMutation({
    mutationFn: (body: AdminPhotoshootVariantCreate) =>
      request<AdminPhotoshootVariantResponse>(`/admin/photoshoots/${photoshootId}/variants`, { method: 'POST', body }),
    onSuccess: invalidate,
  })
  const patchVariant = useMutation({
    mutationFn: ({ modelId, body }: { modelId: string; body: AdminPhotoshootVariantUpdate }) =>
      request<AdminPhotoshootVariantResponse>(`/admin/photoshoots/${photoshootId}/variants/${modelId}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  })
  const deleteVariant = useMutation({
    mutationFn: (modelId: string) =>
      request<void>(`/admin/photoshoots/${photoshootId}/variants/${modelId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  })

  const variants = variantsQuery.data ?? []
  const usedModelIds = new Set(variants.map((v) => v.model_id))
  const addableModels = models.filter((m) => !usedModelIds.has(m.id) && m.is_active)
  const defaultModel = models.find((m) => m.id === defaultModelId)

  const [newModelId, setNewModelId] = useState('')
  const [newExampleUrl, setNewExampleUrl] = useState('')
  const [newSort, setNewSort] = useState(0)

  const error =
    addVariant.error instanceof APIError ? addVariant.error :
    patchVariant.error instanceof APIError ? patchVariant.error :
    deleteVariant.error instanceof APIError ? deleteVariant.error :
    null
  const busy = addVariant.isPending || patchVariant.isPending || deleteVariant.isPending

  const handleAdd = () => {
    if (!newModelId) return
    addVariant.mutate(
      { model_id: newModelId, example_image_url: newExampleUrl.trim() || null, sort_order: newSort },
      { onSuccess: () => { setNewModelId(''); setNewExampleUrl(''); setNewSort(0) } },
    )
  }

  return (
    <div>
      <span className="mb-1 flex items-center gap-1 text-xs font-medium text-ink-500">
        Model variants
        <span className="ml-1 font-normal text-ink-400">
          — extra models the user can choose. Default ({defaultModel ? (defaultModel.display_name || defaultModel.name) : '—'}) is always available even without a variant row.
        </span>
      </span>

      {error && (
        <div className="mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error.message}</div>
      )}

      <div className="rounded-lg border border-ink-100">
        {variantsQuery.isLoading && (
          <div className="px-3 py-3 text-center text-xs text-ink-400">Loading…</div>
        )}
        {!variantsQuery.isLoading && variants.length === 0 && (
          <div className="px-3 py-3 text-center text-xs text-ink-400">No variants — the photoshoot uses only its default model.</div>
        )}
        {variants.map((v) => (
          <VariantRow
            key={v.id}
            variant={v}
            busy={busy}
            onPatch={(body) => patchVariant.mutate({ modelId: v.model_id, body })}
            onRemove={() => deleteVariant.mutate(v.model_id)}
          />
        ))}
      </div>

      {/* Thin translucent separator between the variants list and the
          add-model block — the editor carries a lot now (PX-262). */}
      <div className="my-3 border-t border-ink-200/50" />

      <div className="space-y-2">
        <div className="flex flex-wrap items-end gap-2">
          <select
            value={newModelId}
            onChange={(e) => setNewModelId(e.target.value)}
            className="rounded-md border border-ink-200 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          >
            <option value="">— add model —</option>
            {addableModels.map((m) => (
              <option key={m.id} value={m.id}>
                {(m.display_name || m.name)} ({m.slug}) · ✦{m.credit_cost}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1 text-[11px] text-ink-400">
            sort
            <input
              type="number"
              value={newSort}
              onChange={(e) => setNewSort(Number(e.target.value) || 0)}
              className="w-14 rounded border border-ink-200 px-1.5 py-1 text-xs tabular-nums focus:border-brand-500 focus:outline-none"
            />
          </label>
          <button
            type="button"
            onClick={handleAdd}
            disabled={!newModelId || busy}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Add variant
          </button>
        </div>
        {/* Optional example image for the new variant — upload or paste.
            Prominent (large) full-width drop target. */}
        <MediaDropzone value={newExampleUrl} onChange={setNewExampleUrl} large />
      </div>
    </div>
  )
}

// One variant row (PX-262). Identity + sort + Remove on top, a dedicated
// MediaDropzone for the example image below — drop/upload/paste persists via
// onCommit (NOT per keystroke). Local state mirrors the server value so the
// preview updates instantly; the parent PATCHes on commit.
function VariantRow({
  variant: v,
  busy,
  onPatch,
  onRemove,
}: {
  variant: AdminPhotoshootVariantResponse
  busy: boolean
  onPatch: (body: AdminPhotoshootVariantUpdate) => void
  onRemove: () => void
}) {
  const [exampleUrl, setExampleUrl] = useState(v.example_image_url ?? '')

  return (
    <div className="border-b border-ink-200/50 px-3 py-3 last:border-0">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-ink-900">{v.model_display_name || v.model_name}</div>
          <div className="flex flex-wrap items-center gap-1 font-mono text-[11px] text-ink-400">
            <span>{v.model_slug}</span>
            <span>·</span>
            <span>✦{v.model_credit_cost}</span>
            <span>·</span>
            <span>plans {v.model_allowed_plan_ids.length ? v.model_allowed_plan_ids.join(',') : 'all'}</span>
          </div>
        </div>
        <label className="flex items-center gap-1 text-[11px] text-ink-400">
          sort
          <input
            type="number"
            defaultValue={v.sort_order}
            disabled={busy}
            onBlur={(e) => {
              const val = Number(e.target.value) || 0
              if (val !== v.sort_order) onPatch({ sort_order: val })
            }}
            className="w-14 rounded border border-ink-200 px-1.5 py-1 text-xs tabular-nums focus:border-brand-500 focus:outline-none"
          />
        </label>
        <button
          type="button"
          disabled={busy}
          onClick={onRemove}
          className="rounded-md border border-red-300 px-2 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
        >
          Remove
        </button>
      </div>

      {/* Example image — drop/upload/paste, committed straight to the variant. */}
      <div className="mt-2">
        <MediaDropzone
          value={exampleUrl}
          onChange={setExampleUrl}
          onCommit={(url) => {
            const next = url.trim() || null
            if (next !== (v.example_image_url ?? null)) onPatch({ example_image_url: next })
          }}
          disabled={busy}
        />
      </div>
    </div>
  )
}

// Custom variables (PX-270). Admin-defined SELECT pickers (e.g. "Color") on top
// of the fixed Format/Model controls. Each variable has an editable name +
// placeholder `key` + required flag + a list of options (label shown to the
// user, prompt_value injected into the prompt + hidden from the user). Lives
// only in edit mode (needs a persisted photoshoot id). Mirrors VariantsSection.
const VAR_KEY_RE = /^[a-z][a-z0-9_]{0,63}$/

// Derive a key for a copied variable that's unique within the photoshoot and
// still ≤64 chars (the `(photoshoot_id, key)` unique constraint). Base is an
// existing valid key, so trimming + an `_copy[ N]` suffix stays VAR_KEY_RE-valid.
function uniqueVariableKey(base: string, existing: Set<string>): string {
  const make = (suffix: string) => `${base.slice(0, Math.max(1, 64 - suffix.length))}${suffix}`
  let candidate = make('_copy')
  if (!existing.has(candidate)) return candidate
  for (let i = 2; i < 1000; i += 1) {
    candidate = make(`_copy${i}`)
    if (!existing.has(candidate)) return candidate
  }
  return make(`_copy${existing.size + 1}`)
}

// Input-kind pill toggle (PX-407): Select | Text | Number. Mirrors the
// WeeklyCard / Photo-upload mode-toggle pattern, sized down for the variable row.
function VariableKindToggle({
  value,
  onChange,
  disabled,
}: {
  value: VariableInputKind
  onChange: (k: VariableInputKind) => void
  disabled?: boolean
}) {
  return (
    <div className="inline-flex gap-0.5 rounded-md bg-ink-100 p-0.5">
      {(['select', 'text', 'number'] as const).map((k) => (
        <button
          key={k}
          type="button"
          disabled={disabled}
          onClick={() => { if (k !== value) onChange(k) }}
          className={`rounded px-2 py-0.5 text-[11px] font-medium transition disabled:opacity-50 ${
            value === k ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900'
          }`}
        >
          {k}
        </button>
      ))}
    </div>
  )
}

// Labeled integer field that commits on blur (PX-407) — for text_max_length /
// number_min / number_max. Empty → null. Uncontrolled (defaultValue) like the
// option inputs; remounts with a fresh value when the input kind changes.
function NumberParam({
  label,
  value,
  onCommit,
  disabled,
}: {
  label: string
  value: number | null
  onCommit: (n: number | null) => void
  disabled?: boolean
}) {
  return (
    <label className="flex flex-col gap-0.5 text-[11px] text-ink-400">
      {label}
      <input
        type="number"
        defaultValue={value ?? ''}
        disabled={disabled}
        onBlur={(e) => {
          const raw = e.target.value.trim()
          const next = raw === '' ? null : Number(raw)
          if (next !== value) onCommit(next)
        }}
        className="w-20 rounded border border-ink-200 px-2 py-1 text-xs focus:border-brand-500 focus:outline-none"
      />
    </label>
  )
}

function VariablesSection({ photoshootId }: { photoshootId: string }) {
  const queryClient = useQueryClient()
  const qk = ['admin', 'photoshoots', photoshootId, 'variables']

  const variablesQuery = useQuery({
    queryKey: qk,
    queryFn: () => request<AdminPhotoshootVariableResponse[]>(`/admin/photoshoots/${photoshootId}/variables`),
  })
  const invalidate = () => queryClient.invalidateQueries({ queryKey: qk })

  const addVariable = useMutation({
    mutationFn: (body: AdminPhotoshootVariableCreate) =>
      request<AdminPhotoshootVariableResponse>(`/admin/photoshoots/${photoshootId}/variables`, { method: 'POST', body }),
    onSuccess: invalidate,
  })

  const variables = variablesQuery.data ?? []
  const error = addVariable.error instanceof APIError ? addVariable.error : null

  const [newKey, setNewKey] = useState('')
  const [newName, setNewName] = useState('')
  const [newRequired, setNewRequired] = useState(false)
  const [newKind, setNewKind] = useState<VariableInputKind>('select')
  const [newPlaceholder, setNewPlaceholder] = useState('')
  const [newTextMax, setNewTextMax] = useState('60')
  const [newNumberMin, setNewNumberMin] = useState('')
  const [newNumberMax, setNewNumberMax] = useState('')
  const keyValid = VAR_KEY_RE.test(newKey)
  const newRangeInvalid =
    newKind === 'number' && newNumberMin !== '' && newNumberMax !== '' &&
    Number(newNumberMin) > Number(newNumberMax)

  const resetAddForm = () => {
    setNewKey(''); setNewName(''); setNewRequired(false); setNewKind('select')
    setNewPlaceholder(''); setNewTextMax('60'); setNewNumberMin(''); setNewNumberMax('')
  }

  const handleAdd = () => {
    if (!keyValid || !newName.trim() || newRangeInvalid) return
    const body: AdminPhotoshootVariableCreate = {
      key: newKey, name: newName.trim(), is_required: newRequired,
      sort_order: variables.length, input_kind: newKind, options: [],
    }
    if (newKind !== 'select') body.placeholder = newPlaceholder.trim() || null
    if (newKind === 'text') {
      body.text_max_length = newTextMax.trim() === '' ? null : Number(newTextMax)
    } else if (newKind === 'number') {
      body.number_min = newNumberMin.trim() === '' ? null : Number(newNumberMin)
      body.number_max = newNumberMax.trim() === '' ? null : Number(newNumberMax)
    }
    addVariable.mutate(body, { onSuccess: resetAddForm })
  }

  // Duplicate a whole variable with all its options in one POST (the create
  // endpoint accepts inline options). Key gets a unique `_copy` suffix; the copy
  // lands at the end of the list. Saves re-typing N near-identical options.
  const handleCopy = (src: AdminPhotoshootVariableResponse) => {
    const key = uniqueVariableKey(src.key, new Set(variables.map((x) => x.key)))
    addVariable.mutate({
      key,
      name: `${src.name} (копия)`,
      is_required: src.is_required,
      sort_order: variables.length,
      options: [...src.options]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((o) => ({ label: o.label, prompt_value: o.prompt_value, sort_order: o.sort_order })),
    })
  }

  // Drag-reorder persistence (PX-313). Optimistically rewrite the cache to the
  // new order, then PATCH sort_order only for the rows that actually moved.
  // On either settle we invalidate — refetch confirms success or rolls back.
  const reorderVariables = (sourceId: string, targetId: string) => {
    const ordered = moveItem(variables, sourceId, targetId, (x) => x.id)
    if (ordered === variables) return
    const withSort = ordered.map((x, i) => ({ ...x, sort_order: i }))
    const changed = withSort.filter((x) => variables.find((o) => o.id === x.id)?.sort_order !== x.sort_order)
    if (changed.length === 0) return
    queryClient.setQueryData(qk, withSort)
    Promise.all(
      changed.map((x) => request(`/admin/photoshoots/${photoshootId}/variables/${x.id}`, { method: 'PATCH', body: { sort_order: x.sort_order } })),
    ).then(invalidate, invalidate)
  }

  const reorderOptions = (variableId: string, sourceId: string, targetId: string) => {
    const variable = variables.find((x) => x.id === variableId)
    if (!variable) return
    const ordered = moveItem(variable.options, sourceId, targetId, (o) => o.id)
    if (ordered === variable.options) return
    const withSort = ordered.map((o, i) => ({ ...o, sort_order: i }))
    const changed = withSort.filter((o) => variable.options.find((x) => x.id === o.id)?.sort_order !== o.sort_order)
    if (changed.length === 0) return
    queryClient.setQueryData(qk, variables.map((x) => (x.id === variableId ? { ...x, options: withSort } : x)))
    Promise.all(
      changed.map((o) => request(`/admin/photoshoots/${photoshootId}/variables/${variableId}/options/${o.id}`, { method: 'PATCH', body: { sort_order: o.sort_order } })),
    ).then(invalidate, invalidate)
  }

  return (
    <div>
      <span className="mb-1 flex flex-wrap items-center gap-1 text-xs font-medium text-ink-500">
        Custom variables
        <span className="ml-1 font-normal text-ink-400">
          — extra in-app pickers (e.g. Color). Put <code className="rounded bg-ink-100 px-1 font-mono">{'{key}'}</code> in the Prompt template to position the chosen option's text; otherwise it's appended. The option's prompt text is hidden from users.
        </span>
      </span>

      {error && (
        <div className="mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error.message}</div>
      )}

      <div className="rounded-lg border border-ink-100">
        {variablesQuery.isLoading && (
          <div className="px-3 py-3 text-center text-xs text-ink-400">Loading…</div>
        )}
        {!variablesQuery.isLoading && variables.length === 0 && (
          <div className="px-3 py-3 text-center text-xs text-ink-400">No custom variables — only Format + Model are shown in-app.</div>
        )}
        {variables.map((v) => (
          <div
            key={v.id}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault()
              const sourceId = e.dataTransfer.getData('text/plain')
              if (sourceId) reorderVariables(sourceId, v.id)
            }}
          >
            <VariableRow
              photoshootId={photoshootId}
              variable={v}
              onChanged={invalidate}
              onCopy={handleCopy}
              copying={addVariable.isPending}
              onReorderOptions={reorderOptions}
            />
          </div>
        ))}
      </div>

      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-0.5 text-[11px] text-ink-400">
          key
          <input
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            placeholder="color"
            className="w-28 rounded border border-ink-200 px-2 py-1 font-mono text-xs focus:border-brand-500 focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[11px] text-ink-400">
          name (shown)
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Цвет"
            className="w-40 rounded border border-ink-200 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[11px] text-ink-400">
          type
          <VariableKindToggle value={newKind} onChange={setNewKind} disabled={addVariable.isPending} />
        </label>
        <Toggle label="Required" checked={newRequired} onChange={setNewRequired} />
        {/* Free-input params (PX-407) — placeholder + text cap / number bounds. */}
        {newKind !== 'select' && (
          <label className="flex flex-col gap-0.5 text-[11px] text-ink-400">
            placeholder
            <input
              value={newPlaceholder}
              onChange={(e) => setNewPlaceholder(e.target.value)}
              placeholder="About you"
              className="w-40 rounded border border-ink-200 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none"
            />
          </label>
        )}
        {newKind === 'text' && (
          <label className="flex flex-col gap-0.5 text-[11px] text-ink-400">
            max length
            <input
              type="number"
              value={newTextMax}
              onChange={(e) => setNewTextMax(e.target.value)}
              className="w-20 rounded border border-ink-200 px-2 py-1 text-xs focus:border-brand-500 focus:outline-none"
            />
          </label>
        )}
        {newKind === 'number' && (
          <>
            <label className="flex flex-col gap-0.5 text-[11px] text-ink-400">
              min
              <input
                type="number"
                value={newNumberMin}
                onChange={(e) => setNewNumberMin(e.target.value)}
                className="w-20 rounded border border-ink-200 px-2 py-1 text-xs focus:border-brand-500 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-0.5 text-[11px] text-ink-400">
              max
              <input
                type="number"
                value={newNumberMax}
                onChange={(e) => setNewNumberMax(e.target.value)}
                className="w-20 rounded border border-ink-200 px-2 py-1 text-xs focus:border-brand-500 focus:outline-none"
              />
            </label>
          </>
        )}
        <button
          type="button"
          onClick={handleAdd}
          disabled={!keyValid || !newName.trim() || newRangeInvalid || addVariable.isPending}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Add variable
        </button>
      </div>
      {newKey && !keyValid && (
        <p className="mt-1 text-[11px] text-red-600">key: lowercase letter first, then a–z / 0–9 / _ (≤64 chars)</p>
      )}
      {newRangeInvalid && (
        <p className="mt-1 text-[11px] text-red-600">number: min must be ≤ max</p>
      )}
      {newKind !== 'select' && (
        <p className="mt-1 text-[11px] text-ink-400">
          {newKind === 'text'
            ? 'Text: the user types a free line, injected at {key}.'
            : 'Number: the user enters a whole number within the bounds, injected at {key}.'}
        </p>
      )}
    </div>
  )
}

function VariableRow({
  photoshootId,
  variable,
  onChanged,
  onCopy,
  copying,
  onReorderOptions,
}: {
  photoshootId: string
  variable: AdminPhotoshootVariableResponse
  onChanged: () => void
  onCopy: (variable: AdminPhotoshootVariableResponse) => void
  copying: boolean
  onReorderOptions: (variableId: string, sourceOptionId: string, targetOptionId: string) => void
}) {
  const v = variable
  const base = `/admin/photoshoots/${photoshootId}/variables/${v.id}`

  const patchVariable = useMutation({
    mutationFn: (body: AdminPhotoshootVariableUpdate) => request<AdminPhotoshootVariableResponse>(base, { method: 'PATCH', body }),
    onSuccess: onChanged,
  })
  const deleteVariable = useMutation({
    mutationFn: () => request<void>(base, { method: 'DELETE' }),
    onSuccess: onChanged,
  })
  const addOption = useMutation({
    mutationFn: (body: AdminPhotoshootVariableOptionCreate) => request(`${base}/options`, { method: 'POST', body }),
    onSuccess: onChanged,
  })
  const patchOption = useMutation({
    mutationFn: ({ oid, body }: { oid: string; body: AdminPhotoshootVariableOptionUpdate }) =>
      request(`${base}/options/${oid}`, { method: 'PATCH', body }),
    onSuccess: onChanged,
  })
  const deleteOption = useMutation({
    mutationFn: (oid: string) => request<void>(`${base}/options/${oid}`, { method: 'DELETE' }),
    onSuccess: onChanged,
  })

  const busy =
    patchVariable.isPending || deleteVariable.isPending ||
    addOption.isPending || patchOption.isPending || deleteOption.isPending
  const error = [patchVariable, deleteVariable, addOption, patchOption, deleteOption]
    .map((m) => m.error)
    .find((e) => e instanceof APIError) as APIError | undefined

  const [newLabel, setNewLabel] = useState('')
  const [newPrompt, setNewPrompt] = useState('')
  const [optionsExpanded, setOptionsExpanded] = useState(false)

  const handleAddOption = () => {
    if (!newLabel.trim() || !newPrompt.trim()) return
    addOption.mutate(
      { label: newLabel.trim(), prompt_value: newPrompt.trim(), sort_order: v.options.length },
      { onSuccess: () => { setNewLabel(''); setNewPrompt('') } },
    )
  }

  // Collapse long option lists so a variable with many options doesn't bloat the
  // editor. Show the first 3 (by sort order) collapsed by default; the Add-option
  // form below stays visible either way (PX-312).
  const COLLAPSE_THRESHOLD = 3
  const collapsible = v.options.length > COLLAPSE_THRESHOLD
  const visibleOptions = collapsible && !optionsExpanded ? v.options.slice(0, COLLAPSE_THRESHOLD) : v.options

  return (
    <div className="border-b border-ink-200/50 px-3 py-3 last:border-0">
      <div className="flex flex-wrap items-center gap-2">
        <DragHandle id={v.id} />
        <input
          defaultValue={v.name}
          disabled={busy}
          placeholder="name (shown)"
          onBlur={(e) => { const next = e.target.value.trim(); if (next && next !== v.name) patchVariable.mutate({ name: next }) }}
          className="w-40 rounded border border-ink-200 px-2 py-1 text-sm font-medium focus:border-brand-500 focus:outline-none"
        />
        <input
          defaultValue={v.key}
          disabled={busy}
          placeholder="key"
          onBlur={(e) => { const next = e.target.value.trim(); if (next && next !== v.key) patchVariable.mutate({ key: next }) }}
          className="w-28 rounded border border-ink-200 px-2 py-1 font-mono text-[11px] focus:border-brand-500 focus:outline-none"
        />
        <Toggle label="Required" checked={v.is_required} onChange={(val) => patchVariable.mutate({ is_required: val })} />
        <VariableKindToggle
          value={v.input_kind}
          disabled={busy}
          onChange={(k) => patchVariable.mutate({ input_kind: k })}
        />
        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            disabled={busy || copying}
            onClick={() => onCopy(v)}
            title="Duplicate this variable with all its options"
            className="rounded-md border border-ink-300 px-2 py-1 text-xs font-medium text-ink-600 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Copy
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => deleteVariable.mutate()}
            className="rounded-md border border-red-300 px-2 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
          >
            Remove
          </button>
        </div>
      </div>

      {error && <div className="mt-1 text-[11px] text-red-600">{error.message}</div>}

      {/* PX-407: free-input params for text/number; options editor only for select. */}
      {v.input_kind !== 'select' && (
        <div className="mt-2 flex flex-wrap items-end gap-2 border-l-2 border-ink-100 pl-3">
          <label className="flex flex-col gap-0.5 text-[11px] text-ink-400">
            placeholder
            <input
              defaultValue={v.placeholder ?? ''}
              disabled={busy}
              placeholder="About you"
              onBlur={(e) => {
                const next = e.target.value.trim() || null
                if (next !== v.placeholder) patchVariable.mutate({ placeholder: next })
              }}
              className="w-44 rounded border border-ink-200 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none"
            />
          </label>
          {v.input_kind === 'text' && (
            <NumberParam
              label="max length"
              value={v.text_max_length}
              disabled={busy}
              onCommit={(n) => patchVariable.mutate({ text_max_length: n })}
            />
          )}
          {v.input_kind === 'number' && (
            <>
              <NumberParam label="min" value={v.number_min} disabled={busy}
                onCommit={(n) => patchVariable.mutate({ number_min: n })} />
              <NumberParam label="max" value={v.number_max} disabled={busy}
                onCommit={(n) => patchVariable.mutate({ number_max: n })} />
            </>
          )}
          <span className="text-[11px] text-ink-400">
            user types a {v.input_kind === 'number' ? 'number' : 'value'}, injected at <code className="rounded bg-ink-100 px-1 font-mono">{`{${v.key}}`}</code>
          </span>
        </div>
      )}

      {v.input_kind === 'select' && (
      <div className="mt-2 space-y-1 border-l-2 border-ink-100 pl-3">
        {v.options.length === 0 && (
          <p className="text-[11px] text-ink-400">No options yet — add at least one below.</p>
        )}
        {visibleOptions.map((o) => (
          <div
            key={o.id}
            className="flex flex-wrap items-center gap-2"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault()
              e.stopPropagation()
              const sourceId = e.dataTransfer.getData('text/plain')
              if (sourceId) onReorderOptions(v.id, sourceId, o.id)
            }}
          >
            <DragHandle id={o.id} />
            <input
              defaultValue={o.label}
              disabled={busy}
              placeholder="label (shown)"
              onBlur={(e) => { const next = e.target.value.trim(); if (next && next !== o.label) patchOption.mutate({ oid: o.id, body: { label: next } }) }}
              className="w-36 rounded border border-ink-200 px-2 py-1 text-xs focus:border-brand-500 focus:outline-none"
            />
            <input
              defaultValue={o.prompt_value}
              disabled={busy}
              placeholder="prompt text (hidden)"
              onBlur={(e) => { const next = e.target.value.trim(); if (next && next !== o.prompt_value) patchOption.mutate({ oid: o.id, body: { prompt_value: next } }) }}
              className="w-56 rounded border border-ink-200 px-2 py-1 font-mono text-[11px] focus:border-brand-500 focus:outline-none"
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => patchVariable.mutate({ default_option_id: v.default_option_id === o.id ? null : o.id })}
              title={v.default_option_id === o.id ? 'Default — preselected in the app (click to clear)' : 'Make default (preselected in the app)'}
              aria-label="Toggle default option"
              className={`px-1 text-sm transition ${v.default_option_id === o.id ? 'text-brand-500' : 'text-ink-300 hover:text-ink-500'}`}
            >
              {v.default_option_id === o.id ? '★' : '☆'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => deleteOption.mutate(o.id)}
              className="rounded border border-red-200 px-1.5 py-1 text-xs text-red-600 transition hover:bg-red-50 disabled:opacity-50"
            >
              ×
            </button>
          </div>
        ))}

        {collapsible && (
          <button
            type="button"
            onClick={() => setOptionsExpanded((x) => !x)}
            className="text-[11px] font-medium text-brand-600 transition hover:text-brand-700"
          >
            {optionsExpanded ? 'Свернуть' : `Показать ещё ${v.options.length - COLLAPSE_THRESHOLD}`}
          </button>
        )}

        <div className="flex flex-wrap items-end gap-2 pt-1">
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="label (shown)"
            className="w-36 rounded border border-ink-200 px-2 py-1 text-xs focus:border-brand-500 focus:outline-none"
          />
          <input
            value={newPrompt}
            onChange={(e) => setNewPrompt(e.target.value)}
            placeholder="prompt text (hidden)"
            className="w-56 rounded border border-ink-200 px-2 py-1 font-mono text-[11px] focus:border-brand-500 focus:outline-none"
          />
          <button
            type="button"
            onClick={handleAddOption}
            disabled={!newLabel.trim() || !newPrompt.trim() || busy}
            className="rounded-md border border-brand-300 px-2 py-1 text-xs font-semibold text-brand-700 transition hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Add option
          </button>
        </div>
      </div>
      )}
    </div>
  )
}

// Full-width translucent rule between form sections (PX-275). Lives as a
// grid child spanning both columns so it sits cleanly between rows in the
// 2-col editor grid; `gap-4` gives it breathing room on both sides.
function SectionDivider() {
  return <div className="border-t border-ink-200/50 sm:col-span-2" aria-hidden />
}

function Field({
  label,
  required,
  hint,
  className,
  asDiv,
  children,
}: {
  label: string
  required?: boolean
  hint?: string
  className?: string
  // Render a <div> instead of <label>. REQUIRED when `children` is a
  // MediaDropzone: it owns two inputs (hidden file + url), and a wrapping
  // <label> fires a second activation on the file input that races the
  // dropzone's own click → the picker never opens (PX-263).
  asDiv?: boolean
  children: React.ReactNode
}) {
  const Wrapper = asDiv ? 'div' : 'label'
  return (
    <Wrapper className={`block text-sm ${className ?? ''}`}>
      <span className="mb-1 flex items-center gap-1 text-xs font-medium text-ink-500">
        {label}
        {required && <span className="text-red-500">*</span>}
        {hint && <span className="ml-1 font-normal text-ink-400">— {hint}</span>}
      </span>
      {children}
    </Wrapper>
  )
}
